import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { createClassNames } from '@hrworks/design-system/functions';
import { shouldForwardProp } from '@hrworks/design-system/utilities';

import { TextStyles } from '../Text';
import {
  generateButtonBaseStyles,
  generateButtonDisabledStyles,
  generateButtonVariantStyles,
  generateInteractiveButtonStyles,
} from './Button.styleUtils';
import type { ButtonProps as _ButtonProps } from './Button.types';

type GenerateButtonStylesParams = Pick<
  _ButtonProps,
  'corner' | 'fullWidth' | 'size' | 'color' | 'variant' | 'textAlign'
> & { theme: Theme };

const generateButtonStyles = ({
  theme,
  corner,
  fullWidth,
  size = 'medium',
  color = 'primary',
  variant,
  textAlign,
}: GenerateButtonStylesParams) => {
  return css([
    generateButtonBaseStyles({ theme, size, corner, fullWidth, textAlign, withTypography: variant !== 'unstyled' }),
    variant !== 'unstyled' &&
      css({
        transitionProperty: 'background-color, color, outline-color, opacity',
        transitionDuration: theme.marko.variables.animationDuration.normal,
      }),
    variant && variant !== 'unstyled' && generateButtonVariantStyles({ theme, variant, color }),
    generateButtonDisabledStyles(theme),
    generateInteractiveButtonStyles(theme),
    variant === 'link' && TextStyles.interactiveTextStyles(theme),
  ]);
};

type ButtonProps = {
  $corner: _ButtonProps['corner'];
  $fullWidth: _ButtonProps['fullWidth'];
  $size: Required<_ButtonProps>['size'];
  $color: Required<_ButtonProps>['color'];
  $variant: _ButtonProps['variant'];
  $textAlign: _ButtonProps['textAlign'];
};

const Button = styled('button', {
  shouldForwardProp: (prop: string) => prop !== 'as' && shouldForwardProp(prop),
})<ButtonProps>(({ theme, $corner, $fullWidth, $size, $color, $variant, $textAlign }) =>
  generateButtonStyles({
    theme,
    corner: $corner,
    fullWidth: $fullWidth,
    size: $size,
    color: $color,
    variant: $variant,
    textAlign: $textAlign,
  }),
);

const Icon = styled.span({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  width: '1rem',
  height: '1rem',
  pointerEvents: 'none',
});

const components = { Button, Icon };

export const S = {
  generateInteractiveButtonStyles,
  generateButtonBaseStyles,
  generateButtonDisabledStyles,
  generateButtonVariantStyles,
  generateButtonStyles,
  ...components,
  ...createClassNames('Button', components),
} as const;
